/**
 * CharacterSave - localStorage persistence for character progression and inventory.
 */
import { ITEM_DEFS } from '../loot/ItemDefinitions.js';
import { EQUIP_SLOT_KEYS } from '../loot/Inventory.js';

const SAVE_KEY = 'trixel_character_save';
const CURRENT_VERSION = 5;

export default class CharacterSave {
  constructor() {
    this._saveTimer = null;
  }

  /** Debounced save (100ms). Multiple rapid changes coalesce into one write. */
  scheduleSave(progression, inventory) {
    if (this._saveTimer) clearTimeout(this._saveTimer);
    this._saveTimer = setTimeout(() => {
      this._saveTimer = null;
      this.save(progression, inventory);
    }, 100);
  }

  /** Immediate save to localStorage. */
  save(progression, inventory) {
    const data = {
      version: CURRENT_VERSION,
      timestamp: Date.now(),
      progression: progression.toJSON(),
      inventory: this._serializeInventory(inventory)
    };
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(data));
    } catch (e) {
      console.warn('CharacterSave: failed to write localStorage', e);
    }
  }

  /** Load from localStorage. Returns parsed data or null. */
  load() {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (!raw) return null;
      const data = JSON.parse(raw);
      if (!data) return null;

      // Migrate v1 → v2
      if (data.version === 1) {
        data.inventory = this._migrateV1toV2(data.inventory);
        data.version = 2;
      }
      // Migrate v2 → v3 (expand 8 slots to 16)
      if (data.version === 2) {
        data.inventory = this._migrateV2toV3(data.inventory);
        data.version = 3;
      }
      // Migrate v3 → v4 (add instanceData to items)
      if (data.version === 3) {
        // No structural change needed — old items simply have no instanceData field.
        // _deserializeInventory handles missing instanceData gracefully.
        data.version = 4;
      }
      // Migrate v4 → v5 (add primary attributes + unspentPoints to progression)
      if (data.version === 4) {
        if (data.progression) {
          const level = data.progression.level || 1;
          // Grant retroactive unspent points for all levels gained
          data.progression.attributes = { str: 5, dex: 5, int: 5, con: 5, wis: 5, end: 5 };
          data.progression.unspentPoints = (level - 1) * 3;
        }
        data.version = 5;
      }

      if (data.version !== CURRENT_VERSION) return null;
      return data;
    } catch (e) {
      console.warn('CharacterSave: failed to read localStorage', e);
      return null;
    }
  }

  /** Apply loaded data to game objects. */
  applyTo(data, progression, inventory, combatStats) {
    if (!data) return;
    progression.fromJSON(data.progression);
    progression.applyTo(combatStats);
    combatStats.health = combatStats.maxHealth;
    combatStats.mana = combatStats.maxMana;
    combatStats.stamina = combatStats.maxStamina;
    this._deserializeInventory(data.inventory, inventory);
  }

  /** Clear saved data. */
  clear() {
    localStorage.removeItem(SAVE_KEY);
  }

  /**
   * Migrate v1 save (weapon/armor) to v2 (13 equipment slots).
   * weapon → mainhand, armor → body.
   */
  _migrateV1toV2(inv) {
    if (!inv) return inv;
    const equipped = {};
    for (const key of EQUIP_SLOT_KEYS) {
      equipped[key] = null;
    }
    if (inv.equipped?.weapon) {
      equipped.mainhand = inv.equipped.weapon;
    }
    if (inv.equipped?.armor) {
      // Figure out the correct slot from the item definition
      const armorId = inv.equipped.armor.itemId;
      const def = ITEM_DEFS[armorId];
      const targetSlot = def?.slot || 'body';
      // Map ring → ring1
      const key = targetSlot === 'ring' ? 'ring1' : targetSlot;
      equipped[key] = inv.equipped.armor;
    }
    inv.equipped = equipped;
    return inv;
  }

  /**
   * Migrate v2 save (8 bag slots) to v3 (16 bag slots).
   */
  _migrateV2toV3(inv) {
    if (!inv) return inv;
    if (inv.slots && inv.slots.length < 16) {
      while (inv.slots.length < 16) inv.slots.push(null);
    }
    return inv;
  }

  _serializeInventory(inventory) {
    const serSlot = s => s ? { itemId: s.itemId, quantity: s.quantity, instanceData: s.instanceData || null } : null;
    const equipped = {};
    for (const key of EQUIP_SLOT_KEYS) {
      equipped[key] = serSlot(inventory.equipped[key]);
    }
    return {
      slots: inventory.slots.map(serSlot),
      equipped,
      gold: inventory.gold || 0
    };
  }

  _deserializeInventory(data, inventory) {
    if (!data) return;
    inventory.slots.fill(null);
    for (const key of EQUIP_SLOT_KEYS) {
      inventory.equipped[key] = null;
    }

    if (data.slots) {
      for (let i = 0; i < data.slots.length && i < inventory.slots.length; i++) {
        const s = data.slots[i];
        if (s && ITEM_DEFS[s.itemId]) {
          inventory.slots[i] = { itemId: s.itemId, quantity: s.quantity || 1, def: ITEM_DEFS[s.itemId], instanceData: s.instanceData || null };
        }
      }
    }
    if (data.equipped) {
      for (const key of EQUIP_SLOT_KEYS) {
        const s = data.equipped[key];
        if (s && ITEM_DEFS[s.itemId]) {
          inventory.equipped[key] = { itemId: s.itemId, quantity: s.quantity || 1, def: ITEM_DEFS[s.itemId], instanceData: s.instanceData || null };
        }
      }
    }
    inventory.gold = data.gold || 0;
    inventory._notify();
  }
}
